import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmMgmFeesCatchUpForm, BmItemForm, BmMgmFeeGroupForm } from "../../../pojo/bm-form";
import { BmCalcType } from "../../../pojo/bm-enums";
import { BmItem, BmPeriodEnum } from "../../../pojo/bm-classes";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";
import { BmDefaultFormCreatorService } from "../../general/bm-default-form-creator.service";
/**
 * SERVICE to manage row CATCH UP logic in Management Fees Group
 *
 * 1) Creates catchUp subGroup
 * 2) Subscribes to changes in commitment, mgmFeesPcnt, pcntOfMgmFee, mgmFeeValues, catchUp
 * 3) For each period in catchUp.subGroupData (that is QUARTER), we do:
 *    catchUp(curPeriod) = (commitment(curPeriod) * sumOfMgmFeePcntPrev * sumOfPcntOfMgmFeePrev ) / (numberOfPreviousPeriods) - sumOf( mgmFeeValue(prev) + catchUp(prev) )
 */
export class CatchUpService {

  public static createMgmFeesCatchUpForm(yearsNumber: number, isCatchUpSelected: boolean): FormGroup<BmMgmFeesCatchUpForm> {
    const items: BmItem[] = BmDefaultFormCreatorService.createItemsArray(yearsNumber);

    const itemsFa = new FormArray(items.map((data) => BmCalcHelperService.createBmItemForm(data)));
    return new FormGroup<BmMgmFeesCatchUpForm>({
      isSelected: new FormControl(Boolean(isCatchUpSelected)),
      items: itemsFa,
      totalRow: new FormControl<number>(0),
      totalRowPcnt: new FormControl<number>(0),
    });
  }

  /**
   * For each period in catchUp.subGroupData:
   *  QUARTER:
   *    catchUpValue(currentPeriod) =
   *      (commitment(currentPeriod) * sumOfPcntPrev(MgmFeePcnt) * sumOfPcntPrev(PcntOfMgmFee) / numberOfPreviousPeriods)
   *      - sumOf( mgmFeeValue(prev) + catchUp(prev) )
   *
   *  YEAR:
   *    catchUp(year) = sum of catchUp values of all quarters in that year
   */
  public static catchUpValuesRecalculate(mgmFeeGroupFG: FormGroup<BmMgmFeeGroupForm>): void {
    const catchUpFA = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.items;
    const totalCatchUpRowValue = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.totalRow;
    // references to the others:
    const commitmentFA = mgmFeeGroupFG.controls.subGroups.controls.commitment.controls.items;
    const mgmFeesPcntFA = mgmFeeGroupFG.controls.subGroups.controls.mgmFeesPcnt.controls.items;
    const pcntOfMgmFeeFA = mgmFeeGroupFG.controls.subGroups.controls.pcntOfMgmFees.controls.items;
    const mgmFeeValuesFA = mgmFeeGroupFG.controls.subGroups.controls.mgmFeeValues.controls.items;

    const isPcntOfMgmFeeSelected = mgmFeeGroupFG.controls.subGroups.controls.pcntOfMgmFees.controls.isSelected.getRawValue();

    catchUpFA.controls.forEach((catchUpItemFG) => {
      const raw = catchUpItemFG.getRawValue();
      const period = raw.period;

      if (period.type === BmPeriodEnum.QUARTER) {
        // 1) Get commitment for this exact period
        const commitItem = BmCalcHelperService.findItem(commitmentFA, period.quarterNumber, period.yearNumber);
        const commitVal = commitItem ? commitItem.getRawValue().itemValue : 0;

        // 2) Sum of MgmFeePcnt values of previous quarters in this year
        const sumOfFeePcnt = CatchUpService.sumOfPcntPrevious(mgmFeesPcntFA, period.quarterNumber, period.yearNumber) / 100;

        // 3) Count of previous quarters in this year
        const numberOfPreviousPeriods = CatchUpService.countOfPreviousPeriods(commitmentFA, period.quarterNumber, period.yearNumber);

        // 4) Sum of PcntOfMgmFee values of previous quarters in this year
        // If pcnt of mgm fee is not selected => set numberOfPreviousPeriods
        let sumOfPcntOfMgmFee: number;
        if (isPcntOfMgmFeeSelected) {
          sumOfPcntOfMgmFee = CatchUpService.sumOfPcntPrevious(pcntOfMgmFeeFA, period.quarterNumber, period.yearNumber) / 100;
        } else {
          sumOfPcntOfMgmFee = numberOfPreviousPeriods;
        }

        // 5) Sum of mgmFeeValue + catchUp of previous quarters
        const sumMgmFeeCatchUp = CatchUpService.sumOfMgmFeeAndCatchUp(mgmFeeValuesFA, catchUpFA, period.quarterNumber, period.yearNumber);

        // Final formula
        let newCatchUp = 0;
        if (numberOfPreviousPeriods > 0) {
          newCatchUp = commitVal * sumOfFeePcnt * (sumOfPcntOfMgmFee / numberOfPreviousPeriods) - sumMgmFeeCatchUp;
        }

        // Patch value and calcType
        catchUpItemFG.controls.itemValue.patchValue(newCatchUp, { emitEvent: false });
        catchUpItemFG.controls.calcType.patchValue(BmCalcType.CALCULATED, { emitEvent: false });

      } else if (period.type === BmPeriodEnum.YEAR) {
        // Sum all catchUp values for quarters of this year
        const totalForYear = catchUpFA.controls
          .filter((fg) => {
            const p = fg.getRawValue().period;
            return p.type === BmPeriodEnum.QUARTER && p.yearNumber === period.yearNumber;
          })
          .reduce((sum, fg) => sum + (fg.getRawValue().itemValue ?? 0), 0);

        // Patch value and calcType for YEAR
        catchUpItemFG.controls.itemValue.patchValue(totalForYear, { emitEvent: false });
        catchUpItemFG.controls.calcType.patchValue(BmCalcType.CALCULATED, { emitEvent: false });
      }
    });
    totalCatchUpRowValue.patchValue(BmCalcHelperService.recalculateRowTotal(catchUpFA), { emitEvent: false });
  }


  // =========== Utility methods =============

  /**
   * sumOfPcntPrevious: sums the 'value' for all QUARTER items that are strictly before qNum in the same year yNum.
   */
  private static sumOfPcntPrevious(
    fa: FormArray<FormGroup<BmItemForm>>,
    currentQ: number,
    currentYear: number
  ): number {
    let sum = 0;
    for (let i = 0; i < fa.length; i++) {
      const raw = fa.at(i).getRawValue();
      if (raw.period.yearNumber === currentYear &&
        raw.period.type === BmPeriodEnum.QUARTER &&
        raw.period.quarterNumber < currentQ
      ) {
        sum += Number(raw.itemValue ?? 0);
      }
    }
    return sum;
  }

  /**
   * countOfPreviousPeriods: how many quarters exist with quarterNumber < currentQ in year?
   */
  private static countOfPreviousPeriods(
    commitmentFA: FormArray<FormGroup<BmItemForm>>,
    currentQ: number,
    currentYear: number
  ): number {
    let count = 0;
    for (let i = 0; i < commitmentFA.length; i++) {
      const raw = commitmentFA.at(i).getRawValue();
      if (raw.period.yearNumber === currentYear &&
        raw.period.type === BmPeriodEnum.QUARTER &&
        raw.period.quarterNumber < currentQ
      ) {
        count++;
      }
    }
    return count;
  }

  /**
   * sumOfMgmFeeAndCatchUp for all previous periods.
   * We look up mgmFeeValue and catchUpValue for each quarter < currentQ in year = currentYear
   */
  private static sumOfMgmFeeAndCatchUp(
    mgmFeeFA: FormArray<FormGroup<BmItemForm>>,
    catchUpFA: FormArray<FormGroup<BmItemForm>>,
    currentQ: number,
    currentYear: number
  ): number {
    let sum = 0;
    // sum mgmFee
    for (let i = 0; i < mgmFeeFA.length; i++) {
      const raw = mgmFeeFA.at(i).getRawValue();
      if (raw.period.type === BmPeriodEnum.QUARTER &&
        raw.period.yearNumber === currentYear &&
        raw.period.quarterNumber < currentQ
      ) {
        sum += Number(raw.itemValue ?? 0);
      }
    }
    // sum catchUp
    for (let i = 0; i < catchUpFA.length; i++) {
      const raw = catchUpFA.at(i).getRawValue();
      if (raw.period.type === BmPeriodEnum.QUARTER &&
        raw.period.yearNumber === currentYear &&
        raw.period.quarterNumber < currentQ
      ) {
        sum += Number(raw.itemValue ?? 0);
      }
    }
    return sum;
  }
}
